import { Articulation } from '../guitar/articulation.js'
import type { Fingering, StringNumber } from '../guitar/library.js'
import type { RepeatSpan, Tab, TabStep } from './tab.js'
import { InstrumentType, NoteFinish } from './tab.js'
import { repeatCount, repeatMarks } from './tab-repeats.js'
import { inferRhythm } from './tab-rhythm.js'
import { isTabStringLine, TAB_BLOCK_MAX_ROWS, TAB_BLOCK_MIN_ROWS } from './tab-line.js'

/**
 * ASCII tabs spell a slide `/` (up) or `\` (down); Tablang and this codebase spell it `s`. Direction
 * is not modelled — VexFlow's TabSlide infers it from the frets either side.
 */
const articulationFor = (char: string): Articulation | undefined => {
  if (char === '/' || char === '\\') {
    return Articulation.Slide
  }
  return /[hpsbrt]/.test(char) ? (char as Articulation) : undefined
}

/**
 * `~` and `V` bind to the note just read. Vibrato belongs on the step rather than the fingering —
 * the model spells it `NoteFinish`, and Tablang spells the strong form `V`.
 */
const finishFor = (char: string): NoteFinish | undefined => {
  // `~` is how ASCII tab spells it; `v`/`V` is how Tablang does. Spec section 4 aligns the two
  // vocabularies, and reading only uppercase left `2v` silently dropped while `2V` was kept.
  if (char === '~' || char === 'v') {
    return NoteFinish.Vibrato
  }
  return char === 'V' ? NoteFinish.StrongVibrato : undefined
}

// Extended Fingering type that includes articulation
export interface FingeringWithArticulation extends Fingering {
  articulation?: Articulation
}

export interface ParsedTabLine {
  stringNum: StringNumber
  positions: {
    position: number
    fret: number | null
    articulation?: Articulation
    finish?: NoteFinish
    ghost?: boolean
  }[]
  originalContent: string
  /**
   * The line's opening `|`, consumed along with the string label. Column 0 of `originalContent` is
   * the character AFTER it, so a `|:` or `|o` that opens a block has no barline column of its own
   * — see `repeatBarlineColumns`.
   */
  opensWithBar: boolean
  /** The closing `|`, stripped from `originalContent` along with anything trailing it. */
  closesWithBar: boolean
}

/** One system's string rows, with the line it ends on so a following count row can find it. */
interface TabSection {
  lines: string[]
  endLine: number
}

/** A repeat mark resolved to a step index within one section. */
interface StepRepeatMark {
  /** `begin` indexes the first step INSIDE the repeat; `end` the last one. */
  kind: 'begin' | 'end'
  stepIndex: number
}

export interface AsciiTabParseOptions {
  /** Whether to include muted strings (x) as null frets */
  includeMutedStrings?: boolean
  /** Whether to ignore timing markers like |, -, etc. */
  ignoreTimingMarkers?: boolean
  /**
   * Quarter notes per measure for duration inference — 4 for 4/4, 3 for 3/4. Omit to let the parser
   * try both simple (4) and compound (6) time and keep whichever closes.
   */
  quarterNotesPerMeasure?: number
}

/**
 * Parses ASCII guitar and bass tablature into Tab format
 *
 * Supports standard 6-string guitar notation:
 * e|--3--5--7--|
 * B|--3--5--7--|
 * G|--4--6--8--|
 * D|--5--7--9--|
 * A|--5--7--9--|
 * E|--3--5--7--|
 *
 * And 4-string bass notation:
 * G|--3--5--7--|
 * D|--5--7--9--|
 * A|--5--7--9--|
 * E|--3--5--7--|
 */
export class AsciiTabParser {
  private readonly guitarStringIdentifiers = {
    e: 1, // High E string
    b: 2, // B string
    B: 2, // B string (uppercase)
    g: 3, // G string
    G: 3, // G string (uppercase)
    d: 4, // D string
    D: 4, // D string (uppercase)
    a: 5, // A string
    A: 5, // A string (uppercase)
    E: 6, // Low E string
  }

  private readonly bassStringIdentifiers = {
    g: 1, // High G string
    G: 1, // High G string (uppercase)
    d: 2, // D string
    D: 2, // D string (uppercase)
    a: 3, // A string
    A: 3, // A string (uppercase)
    e: 4, // Low E string
    E: 4, // Low E string (uppercase)
  }

  // Will be set during parsing based on detected instrument type
  private stringIdentifiers: Record<string, number> = this.guitarStringIdentifiers

  /**
   * Parse ASCII tab text into Tab format
   */
  parse(asciiTab: string, options: AsciiTabParseOptions = {}): Tab {
    const lines = asciiTab.split('\n').map(line => line.trim())

    // Detect instrument type based on the strings present
    const instrumentType = this.detectInstrumentType(lines)
    this.stringIdentifiers = instrumentType === InstrumentType.Bass
      ? this.bassStringIdentifiers
      : this.guitarStringIdentifiers

    if (!this.isRenderableWidth(lines)) {
      return { steps: [], instrument: instrumentType, source: asciiTab }
    }

    // Group tab lines by sections
    const tabSections = this.extractTabSections(lines)

    // Parse each section and concatenate all steps
    const allSteps: TabStep[] = []
    /** Spans in source order, each remembering the line its end mark was read on. */
    const spans: (RepeatSpan & { endLine: number })[] = []
    /**
     * The step a repeat opened on, waiting for its close. Held ACROSS sections: a span that opens in
     * one system and closes two systems later is ordinary engraving, and step indices are global.
     * Nesting is a non-goal, so a second open before a close replaces the first.
     */
    let openStep: number | undefined

    for (const section of tabSections) {
      const parsedLines = section.lines.map(line => this.parseTabLine(line, options))
      const { steps: sectionSteps, repeatMarks: sectionRepeatMarks } = this.convertToTabSteps(parsedLines, options)
      /**
       * The section boundary is a system break the transcriber wrote, and concatenating the steps
       * used to be where it was lost — two stanzas came back as one continuous line. Marked on the
       * first step of every section that follows one which produced steps, so a section that
       * yielded nothing hands the break on rather than swallowing it.
       */
      const first = sectionSteps[0]
      if (first && allSteps.length > 0) {
        first.startsSystem = true
      }
      const offset = allSteps.length
      allSteps.push(...sectionSteps)

      for (const mark of sectionRepeatMarks) {
        if (mark.kind === 'begin') {
          openStep = offset + mark.stepIndex
          continue
        }
        /**
         * A close with nothing open is dropped. Not for want of a reading: standard notation defines
         * a lone `:|` precisely, as repeating from the beginning of the piece. This is a deliberate
         * refusal of that defined reading, because what we parse is a pasted FRAGMENT, which has no
         * beginning of piece — the fragment's first step is not the work's first step, and in a
         * multi-stanza paste "from the beginning" would bracket every stanza. Zero instances across
         * the fixture corpus. Spec 2026-08-30 §6.
         */
        if (openStep !== undefined) {
          spans.push({ startStep: openStep, endStep: offset + mark.stepIndex, endLine: section.endLine })
          openStep = undefined
        }
      }
    }
    // An open with no close is dropped too, and that one needs no argument: notation gives it no
    // reading at all. Half a bracket is not a section.

    /**
     * The confidence gate is all-or-nothing per run, but a chart splits one passage across many
     * systems and each becomes its own section. Gating them independently and then flattening gives
     * a single Tab where some notes are timed and some are not — the half-timed result the gate
     * exists to prevent, just reached one level up. If any section declined, none of them count.
     */
    const hasNote = (step: TabStep) =>
      Array.isArray(step.fingering) ? step.fingering.length > 0 : Boolean(step.fingering)
    const noteSteps = allSteps.filter(hasNote)
    const timed = noteSteps.filter(step => step.duration !== undefined).length
    if (timed > 0 && timed < noteSteps.length) {
      for (const step of allSteps) {
        // `delete`, not `= undefined`: assigning leaves the key present, which serialises into
        // snapshots as `"duration": undefined` and changes the shape callers see.
        delete step.duration
      }
    }

    this.attachCounts(spans, lines)

    return {
      steps: allSteps,
      instrument: instrumentType,
      // The argument, not the trimmed `lines` — preservation is byte-for-byte or it is not
      // preservation. This is the single write point for `source`, so every caller of
      // `parseAsciiTab` gets it without having to remember.
      source: asciiTab,
      // Absent rather than empty when the tab has none, so a tab with no repeats serialises exactly
      // as it did before this field existed.
      ...(spans.length > 0 ? { repeats: spans.map(({ endLine: _endLine, ...span }) => span) } : {}),
    }
  }

  /**
   * Attach each `xN` ornament row to the last span that ended at or before it, mutating the spans.
   *
   * Ornament rows are otherwise invisible to this parser, which reads string rows only. A count with
   * no span before it is preserved in `source` and modelled nowhere: it may be a count for a section
   * the transcriber never bracketed, and inventing a span to hold it would assert structure that is
   * not in the source. A span takes the first count that reaches it, so a second count resolving to
   * the same span — which only a malformed paste produces — cannot silently overwrite the first.
   */
  private attachCounts(spans: (RepeatSpan & { endLine: number })[], lines: string[]): void {
    for (const [lineIndex, line] of lines.entries()) {
      const count = repeatCount(line)
      if (count === undefined) {
        continue
      }
      for (let i = spans.length - 1; i >= 0; i--) {
        if (spans[i].endLine <= lineIndex) {
          if (spans[i].count === undefined) {
            spans[i].count = count
          }
          break
        }
      }
    }
  }

  /**
   * Row count decides the instrument; labels break the ties. Spec 2026-08-25 §6.
   *
   * The previous rule — "if we see a `b` label it is a guitar" — is wrong for a five-string bass,
   * whose lowest string is a low B. Width answers 5, 6, 7 and 8 outright. It cannot answer 4 (a
   * bass, or the top four strings of a guitar) and it cannot answer a run with no readable period;
   * in both cases the labels decide, which is the old heuristic kept for the cases it was right
   * about.
   */
  private detectInstrumentType(lines: string[]): InstrumentType {
    const width = this.stringRowWidth(lines)
    if (width === 5) {
      return InstrumentType.Bass
    }
    if (width >= 6 && width <= TAB_BLOCK_MAX_ROWS) {
      return InstrumentType.Guitar
    }
    const bassStrings = new Set(['g', 'd', 'a', 'e'])
    const labels = new Set(this.stringLabels(lines))
    return labels.size > 0 && labels.size <= 4 && [...labels].every(l => bassStrings.has(l))
      ? InstrumentType.Bass
      : InstrumentType.Guitar
  }

  /** The label on a string row, lowercased. Empty for a bare row, which carries none. */
  private rowLabel(line: string): string {
    const match = /^\s*([a-gA-G])[#b]?\s*[|:-]/.exec(line)
    return match ? match[1].toLowerCase() : ''
  }

  /** Labels present on the string rows, lowercased, in source order. */
  private stringLabels(lines: string[]): string[] {
    return lines.flatMap(line => (isTabStringLine(line) ? [this.rowLabel(line)] : []))
  }

  /** The labels of the longest run of consecutive string rows, in source order. */
  private longestRunLabels(lines: string[]): string[] {
    let longest: string[] = []
    let run: string[] = []
    for (const line of lines) {
      if (!isTabStringLine(line)) {
        run = []
        continue
      }
      run.push(this.rowLabel(line))
      if (run.length > longest.length) {
        longest = [...run]
      }
    }
    return longest
  }

  /**
   * The width of one system: the period of the run's label sequence, not the run's length.
   *
   * The two are the same only for a single-system tab. `chart-parser` strips the blank lines
   * between systems before handing the run over, so a transcription arrives as one run whose length
   * is a *multiple* of its width — 96 rows of six-string guitar in
   * `chart-parser/src/fixtures/real-tab-and-chords.txt`. Measuring the run there reads 96, and
   * measuring a truncated run reads 8; the width is 6 either way, and only the period finds it.
   *
   * It matters because the guard below is what keeps a seven- or eight-string tab away from a
   * renderer that has no tuning for it. A two-system seven-string run is 14 rows: not 7, so it
   * slipped the guard, and its low B was then mapped onto string 2 — the high B, three octaves up,
   * colliding with the genuine B-string row.
   *
   * Falls back to the run length when no width in 4..8 divides it with a matching label sequence,
   * which is the honest answer for a run that is not periodic at all.
   */
  private stringRowWidth(lines: string[]): number {
    const labels = this.longestRunLabels(lines)
    const candidates = Array.from(
      { length: TAB_BLOCK_MAX_ROWS - TAB_BLOCK_MIN_ROWS + 1 },
      (_, i) => TAB_BLOCK_MIN_ROWS + i
    )
    const period = candidates.find(
      width =>
        labels.length >= width &&
        labels.length % width === 0 &&
        labels.every((label, i) => label === labels[i % width])
    )
    return period ?? labels.length
  }

  /**
   * `StringNumber` is 1..6. A five-string bass and a seven- or eight-string guitar cannot be
   * represented, and inventing a string 7 would corrupt every downstream consumer. They preserve
   * through `source` and render raw.
   *
   * The test is for the unrepresentable widths in spec §6's table, not for "exactly 4 or 6". A run
   * narrower than a block — a caller handing the parser a two-row excerpt — predates this design,
   * cannot produce an out-of-range string number, and is left alone.
   */
  private isRenderableWidth(lines: string[]): boolean {
    const unrepresentable = new Set([5, 7, 8])
    return !unrepresentable.has(this.stringRowWidth(lines))
  }

  /**
   * Extract sections of tab lines from input text
   * Each section is a group of tab lines that represent a complete tablature section
   */
  private extractTabSections(lines: string[]): TabSection[] {
    const sections: TabSection[] = []
    let currentSection: string[] = []
    /** Index in `lines` of the last row taken into `currentSection` — where a count row looks back to. */
    let currentEndLine = -1
    const stringsSeen = new Set<string>()
    const close = () => {
      if (currentSection.length > 0) {
        sections.push({ lines: currentSection, endLine: currentEndLine })
        currentSection = []
        stringsSeen.clear()
      }
    }

    for (const [lineIndex, line] of lines.entries()) {
      if (!line) {
        // Empty line might indicate a section break
        close()
        continue
      }

      // Check if this line looks like a tab line (single letter followed by | or -)
      const potentialTabLinePattern = /^([a-zA-Z])\s*[\|\-]/
      const match = potentialTabLinePattern.exec(line)

      if (match) {
        // This looks like a tab line, validate the string identifier
        const stringLetter = match[1]
        if (this.stringIdentifiers[stringLetter] !== undefined) {
          // If we've already seen this string in the current section, it might be a new section
          if (stringsSeen.has(stringLetter)) {
            // Complete the current section and start a new one
            close()
          }

          stringsSeen.add(stringLetter)
          currentSection.push(line)
          currentEndLine = lineIndex
        } else {
          throw new Error(`Unknown string identifier: ${stringLetter}`)
        }
      } else if (currentSection.length > 0 && stringsSeen.size >= 4) {
        // If we have enough strings in the current section and hit a non-tab line,
        // complete the current section
        close()
      }
    }

    // Add the last section if it exists
    close()

    return sections
  }

  /**
   * Parse a single tab line into positions
   */
  private parseTabLine(line: string, options: AsciiTabParseOptions): ParsedTabLine {
    // Extract string identifier
    const stringMatch = line.match(/^([ebBgGdDaAE])\s*[\|\-]/)
    if (!stringMatch) {
      throw new Error(`Invalid tab line format: ${line}`)
    }

    const stringLetter = stringMatch[1]
    const stringNumber = this.getStringNumber(stringLetter)

    // Extract the tab content after the string identifier and initial marker
    const tabContent = line.substring(stringMatch[0].length)

    // Remove trailing pipe and any non-tab content after it (like repeat indicators "x4")
    // A closing pipe followed by whitespace or non-tab chars should be stripped
    // Note: "|" followed by whitespace then anything is not valid tab content
    const cleanContent = tabContent.replace(/\|(\s.*)?$/, '')
    /**
     * Both of a line's outer barlines are consumed before a column index exists: the opening one by
     * the label match above, the closing one by the strip above that. Neither is a measure boundary
     * — no step follows the closing bar, and column 0 already opens the first measure — but both are
     * real barlines, and a repeat sign is defined by its position against one. Recorded rather than
     * folded into `barlineColumns`, which rhythm inference reads and which must keep meaning exactly
     * what it means today.
     */
    const opensWithBar = stringMatch[0].endsWith('|')
    const closesWithBar = cleanContent.length < tabContent.length

    const positions: ParsedTabLine['positions'] = []
    let ghostAtNextFret = false
    /** An articulation read before its fret, waiting for the note it produces. */
    let pendingArticulation: Articulation | undefined

    for (let i = 0; i < cleanContent.length; i++) {
      const char = cleanContent[i]

      if (char === '-') {
        // Skip timing markers unless they represent a note
        continue
      }

      if (char === '(') {
        // A ghost note: `(3)`. Only a lone fret in parens counts — parens that bracket a whole
        // phrase, `(13\12------13\10)`, are a transcriber's annotation, not a ghost note. Let the
        // digit branch below read the fret; the closing paren falls through to the ignored case.
        ghostAtNextFret = /^\d+\)/.test(cleanContent.slice(i + 1))
        continue
      }

      if (char === 'x' || char === 'X') {
        // Muted string
        if (options.includeMutedStrings) {
          positions.push({ position: i, fret: null })
        }
        continue
      }

      /**
       * An articulation with no fret before it, `--h3--`. Tablang spells this too (production
       * `articulation fret`), and under the arriving convention it is meaningful: the note is
       * hammered, it just has no predecessor on this line to be hammered from. The renderer's
       * `if (!prevNote) continue` then draws no connector, which is the correct outcome.
       *
       * Bind it only when a fret actually follows, so a stray character stays ignored.
       */
      const leadingArticulation = articulationFor(char)
      if (leadingArticulation && /\d/.test(cleanContent[i + 1] ?? '')) {
        pendingArticulation = leadingArticulation
        continue
      }

      if (/\d/.test(char)) {
        /**
         * A run of frets joined by articulations, `2h3/4`.
         *
         * The articulation binds to the fret that FOLLOWS it, not the one before it. A hammer-on
         * sounds its target — in `2h3` the 3 is what gets hammered, and in `3/4` the 4 is what gets
         * slid into — so the articulation describes how the arriving note is produced. Tablang's
         * grammar (`tablang.jison`, production `frets articulation fret`) and `render-tab.ts`
         * (which draws the connector from the previous note to the one carrying the articulation)
         * both already read it this way; this parser was the sole outlier, which cost every figure
         * its first connector and drew each of the rest a note early.
         */
        let pending = pendingArticulation
        pendingArticulation = undefined
        let cursor = i
        let isFirstFret = true

        for (;;) {
          let fretStr = ''
          const fretStart = cursor
          let j = cursor
          while (j < cleanContent.length && /\d/.test(cleanContent[j])) {
            fretStr += cleanContent[j]
            j++
          }

          const fret = parseInt(fretStr, 10)

          // The articulation after this fret belongs to the *next* one, so hold it rather than
          // attaching it here.
          let nextArticulation: Articulation | undefined
          if (j < cleanContent.length) {
            nextArticulation = articulationFor(cleanContent[j])
            if (nextArticulation) {
              j++
            }
          }

          // `~`/`V` still bind to the note just read — a finish is not a connector.
          let finish: NoteFinish | undefined
          if (j < cleanContent.length) {
            finish = finishFor(cleanContent[j])
            if (finish) {
              j++
            }
          }

          positions.push({
            position: fretStart,
            fret,
            articulation: pending,
            finish,
            ...(isFirstFret && ghostAtNextFret ? { ghost: true } : {}),
          })
          if (isFirstFret) {
            ghostAtNextFret = false
            isFirstFret = false
          }

          cursor = j

          if (!nextArticulation) {
            break
          }
          if (j < cleanContent.length && /\d/.test(cleanContent[j])) {
            pending = nextArticulation
            continue
          }
          // `3h(5)` — the arriving note is a ghost note, so let the `(` branch read it and carry
          // the articulation over. Anything else is a dangling articulation connecting nothing.
          if (cleanContent[j] === '(') {
            pendingArticulation = nextArticulation
          }
          break
        }

        // Skip the characters we consumed
        i = cursor - 1
        continue
      }

      // Other characters like |, space, etc. are ignored
    }

    return {
      stringNum: stringNumber,
      positions,
      originalContent: cleanContent,
      opensWithBar,
      closesWithBar,
    }
  }

  /**
   * Convert parsed tab lines into TabStep array
   */
  private convertToTabSteps(
    parsedLines: ParsedTabLine[],
    options: AsciiTabParseOptions = {}
  ): { steps: TabStep[]; repeatMarks: StepRepeatMark[] } {
    if (parsedLines.length === 0) {
      return { steps: [], repeatMarks: [] }
    }

    // Special case: if all lines contain only dashes (timing markers), create timing steps
    const allDashesPattern = parsedLines.every(line => /^-+$/.test(line.originalContent))
    if (allDashesPattern) {
      const dashCount = parsedLines[0].originalContent.length
      if (dashCount === 6) {
        // Create 5 timing steps for 6 dashes
        return {
          steps: [{ fingering: [] }, { fingering: [] }, { fingering: [] }, { fingering: [] }, { fingering: [] }],
          repeatMarks: [],
        }
      }
    }

    // First, detect pause ranges in the original content
    const pauseRanges = this.detectPauseRanges(parsedLines)

    // Find the maximum position across all strings
    const allPositions = parsedLines.flatMap(line => line.positions.map(pos => pos.position))
    const maxContentLength = Math.max(...parsedLines.map(line => line.originalContent.length))
    // Use the maximum of either the highest note position or the content length - 1
    const maxPosition = Math.max(
      allPositions.length > 0 ? Math.max(...allPositions) : 0,
      maxContentLength - 1
    )

    const steps: TabStep[] = []
    /**
     * The column each step was emitted at. A step's index is not its column — a column with neither
     * a note nor a pause emits nothing — and rhythm inference reasons in columns.
     */
    const stepColumns: number[] = []

    // A `|` occupies a column of its own, so no step is ever emitted at the barline column itself.
    // The step that *begins* the measure is therefore the first one at or after that column.
    const barlines = this.barlineColumns(parsedLines)
    let nextBarline = 0
    const startsMeasureAt = (column: number): boolean => {
      if (nextBarline >= barlines.length || column < barlines[nextBarline]) {
        return false
      }
      // Consume every barline this step has already passed, so a `||` repeat marks one step, not two.
      while (nextBarline < barlines.length && column >= barlines[nextBarline]) {
        nextBarline++
      }
      return true
    }

    // If there are no regular positions but content exists, create timing-only steps
    if (allPositions.length === 0) {
      // Check if we have content that should create timing steps
      const hasOnlyDashes = parsedLines.every(line => /^-+$/.test(line.originalContent))
      if (hasOnlyDashes && maxContentLength >= 6) {
        // For 6 dashes (------), create 3 timing steps
        const timingSteps = 3
        for (let i = 0; i < timingSteps; i++) {
          steps.push({
            fingering: [],
          })
        }
        return { steps, repeatMarks: [] }
      }
    }

    // For each position, collect all the fingerings from all strings
    for (let pos = 0; pos <= maxPosition; pos++) {
      const fingerings: FingeringWithArticulation[] = []

      for (const line of parsedLines) {
        const noteAtPosition = line.positions.find(p => p.position === pos)
        if (noteAtPosition) {
          if (noteAtPosition.fret !== null) {
            const fingering: FingeringWithArticulation = {
              str: line.stringNum,
              fret: noteAtPosition.fret,
            }
            
            // Add articulation to the fingering if present
            if (noteAtPosition.articulation) {
              fingering.articulation = noteAtPosition.articulation
            }

            if (noteAtPosition.ghost) {
              fingering.ghost = true
            }

            fingerings.push(fingering)
          } else if (options.includeMutedStrings) {
            // Include muted strings when option is enabled
            fingerings.push({
              str: line.stringNum,
              fret: null,
            })
          }
        }
      }

      // Check if this position is within a pause range
      const pauseRange = pauseRanges.find(range => pos >= range.start && pos < range.end)

      if (pauseRange && fingerings.length === 0) {
        // Only add pause on the first position of the range
        if (pos === pauseRange.start) {
          // A measure may open on a rest, so the first pause step of the range carries the mark.
          const startsMeasure = startsMeasureAt(pos)
          for (let i = 0; i < pauseRange.beats; i++) {
            steps.push({
              fingering: [],
              ...(i === 0 && startsMeasure ? { startsMeasure: true } : {}),
            })
            stepColumns.push(pos)
          }
        }
        // Skip other positions in the pause range
      } else if (fingerings.length > 0) {
        // A step carries one finish; the first string that declares one wins. Vibrato on a chord is
        // notated once in practice, so this does not lose information.
        const finish = parsedLines
          .map(line => line.positions.find(position => position.position === pos)?.finish)
          .find(Boolean)

        // Create a step with fingerings
        steps.push({
          fingering: fingerings,
          ...(finish ? { finish } : {}),
          ...(startsMeasureAt(pos) ? { startsMeasure: true } : {}),
        })
        stepColumns.push(pos)
      }
    }

    // Onsets are columns that actually carry a note; empty columns are rests and take no duration.
    const onsetSteps = steps.flatMap((step, index) =>
      (Array.isArray(step.fingering) ? step.fingering.length > 0 : Boolean(step.fingering)) ? [index] : []
    )
    /**
     * Coalesce adjacent barlines, exactly as `startsMeasureAt` does. A `||` repeat is one boundary;
     * passing both columns injects a 1-wide phantom measure that drags the confidence down and
     * silently costs a well-formed chart its rhythm.
     */
    const measureStarts = barlines.filter((column, index) => index === 0 || column - barlines[index - 1] > 1)
    const { durations } = inferRhythm({
      runStart: 0,
      // Column 0 opens a measure. `parseTabLine` strips the label's leading `|`, so the barline that
      // starts the run is never in `originalContent` — without it a two-bar run looks like one bar.
      measureStarts: measureStarts[0] === 0 ? measureStarts : [0, ...measureStarts],
      runEnd: maxPosition + 1,
      onsets: onsetSteps.map(index => stepColumns[index]),
      quarterNotesPerMeasure: options.quarterNotesPerMeasure,
    })
    // Empty when the run failed the confidence gate; the renderer then keeps its 'q' default.
    durations.forEach((duration, index) => {
      steps[onsetSteps[index]].duration = duration
    })

    return { steps, repeatMarks: this.stepRepeatMarks(parsedLines, stepColumns) }
  }

  /**
   * The section's repeat marks, resolved from columns to step indices.
   *
   * A `begin` indexes the first step AFTER the barline run, and an `end` the last step BEFORE it —
   * the two ends of the span, both inside it. A mark with no step on its inside resolves to nothing:
   * a repeat that brackets no music is not a repeat.
   */
  private stepRepeatMarks(parsedLines: ParsedTabLine[], stepColumns: number[]): StepRepeatMark[] {
    const contents = parsedLines.map(line => line.originalContent)
    const { columns, closingBarColumn } = this.repeatBarlineColumns(parsedLines)
    const marks = repeatMarks(contents, columns, closingBarColumn)
    return marks.flatMap<StepRepeatMark>(mark => {
      const resolved: StepRepeatMark[] = []
      // An `o||o` closes one repeat and opens the next, in that order.
      if (mark.ends) {
        let stepIndex = -1
        for (let i = stepColumns.length - 1; i >= 0; i--) {
          if (stepColumns[i] < mark.startColumn) {
            stepIndex = i
            break
          }
        }
        if (stepIndex >= 0) {
          resolved.push({ kind: 'end', stepIndex })
        }
      }
      if (mark.begins) {
        const stepIndex = stepColumns.findIndex(column => column > mark.endColumn)
        if (stepIndex >= 0) {
          resolved.push({ kind: 'begin', stepIndex })
        }
      }
      return resolved
    })
  }

  /**
   * Every column a barline stands on, including the two that `barlineColumns` cannot see: the
   * opening bar the label match consumed, at the notional column -1, and the closing bar the content
   * strip removed, one past the widest row.
   *
   * Repeat detection needs both, because the two spellings put their dots on opposite sides of them:
   * `G|:` opens a block on the consumed bar, and `:|` closes one on the stripped bar. Without them
   * the `:` spelling would read as no repeat at all while `||o` / `o||` read correctly — the parser
   * reading spelling rather than notation, which is the failure this whole design guards against.
   *
   * The closing one is reported separately because it is a fiction: rows are padded by eye, so the
   * closing bars stand at each row's own end and only the widest row's is at this column. Its dots
   * have to be read per row, which is what `repeatMarks` uses the column to recognise.
   */
  private repeatBarlineColumns(parsedLines: ParsedTabLine[]): { columns: number[]; closingBarColumn?: number } {
    const closes = parsedLines.every(line => line.closesWithBar)
    const closingBarColumn = closes ? Math.max(...parsedLines.map(line => line.originalContent.length)) : undefined
    return {
      columns: [
        ...(parsedLines.every(line => line.opensWithBar) ? [-1] : []),
        ...this.barlineColumns(parsedLines),
        ...(closingBarColumn === undefined ? [] : [closingBarColumn]),
      ],
      closingBarColumn,
    }
  }

  /**
   * Columns where every string carries a `|`. Agreement across all strings is what separates a
   * measure boundary from a stray character, and it is cheap: a barline is a vertical thing.
   */
  private barlineColumns(parsedLines: ParsedTabLine[]): number[] {
    if (parsedLines.length === 0) {
      return []
    }
    const columnsPerLine = parsedLines.map(
      line => new Set([...line.originalContent].flatMap((char, index) => (char === '|' ? [index] : [])))
    )
    const [first, ...rest] = columnsPerLine
    return [...first].filter(column => rest.every(columns => columns.has(column))).sort((a, b) => a - b)
  }

  /**
   * Detect ranges where all strings have multiple consecutive dashes
   */
  private detectPauseRanges(parsedLines: ParsedTabLine[]): { start: number; end: number; beats: number }[] {
    if (parsedLines.length === 0) {
      return []
    }

    const maxLength = Math.max(...parsedLines.map(line => line.originalContent.length))
    const pauseRanges: { start: number; end: number; beats: number }[] = []

    for (let pos = 0; pos < maxLength; pos++) {
      // Check if all strings have consecutive dashes starting at this position
      let minConsecutiveDashes = Infinity
      let allStringsHaveDashes = true

      for (const line of parsedLines) {
        const dashCount = this.countConsecutiveDashesAtPosition(line.originalContent, pos)
        if (dashCount < 2) {
          // Require at least 2 consecutive dashes for a pause
          allStringsHaveDashes = false
          break
        }
        minConsecutiveDashes = Math.min(minConsecutiveDashes, dashCount)
      }

      if (allStringsHaveDashes && minConsecutiveDashes >= 2) {
        // Found a pause range
        pauseRanges.push({
          start: pos,
          end: pos + minConsecutiveDashes,
          beats: 1, // Create one pause step per dash sequence
        })
        // Skip ahead to avoid overlapping ranges
        pos += minConsecutiveDashes - 1
      }
    }

    return pauseRanges
  }

  /**
   * Count consecutive dashes at a specific position in the tab content
   */
  private countConsecutiveDashesAtPosition(content: string, position: number): number {
    if (position >= content.length || content[position] !== '-') {
      return 0
    }

    let count = 0
    for (let i = position; i < content.length && content[i] === '-'; i++) {
      count++
    }

    return count
  }


  /**
   * Map string letter to string number
   * For guitar: 1-6, where 1 is high E
   * For bass: 1-4, where 1 is high G
   */
  private getStringNumber(stringLetter: string): StringNumber {
    const stringNumber = this.stringIdentifiers[stringLetter]
    if (stringNumber === undefined) {
      throw new Error(`Unknown string identifier: ${stringLetter}`)
    }
    return stringNumber as StringNumber
  }
}

/**
 * Parse ASCII guitar tab text into Tab format
 */
export const parseAsciiTab = (asciiTab: string, options?: AsciiTabParseOptions): Tab => {
  const parser = new AsciiTabParser()
  return parser.parse(asciiTab, options)
}
